import React from 'react';
import { StyleSheet, View } from 'react-native';
import { BodyText, Button, TextField, theme } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

export const CreditOverviewPromoCodeForm = ({
  buttonPlacement = 'right',
  onPressApplyPromoCode,
  hasPromoCodeError,
  promoCode,
  isSubmittingPromoCode,
  setPromoCode,
  handleShowScanner,
}: CreditOverviewPromoCodeFormProps) => {
  const onPress = React.useCallback(() => {
    onPressApplyPromoCode(promoCode);
  }, [promoCode, onPressApplyPromoCode]);

  const shouldDisableApplyBtn = promoCode.trim() === '';
  const { t } = useLocalizationContext();
  const isBottomButton = buttonPlacement === 'bottom';
  const cameraIcon = isBottomButton ? 'IconCamera' : undefined;
  const inputPadding = !isBottomButton ? styles.inputPadding : undefined;
  const buttonProps: React.ComponentProps<typeof Button> = {
    testID: 'account.promo-code-button',
    palette: 'primary',
    accessibilityLabel: t('account.credit.a11y-label'),
    accessibilityHint: t('account.credit.a11y-label'),
    accessibilityRole: 'button',
    accessibilityState: { disabled: shouldDisableApplyBtn },
    disabled: shouldDisableApplyBtn,
    isLoading: isSubmittingPromoCode,
    onPress,
  };

  return (
    <View>
      <View style={styles.promoCodeWrapper}>
        <View style={[styles.promoCodeInputWrapper, inputPadding]}>
          <TextField
            testID="account.promo-code-field"
            placeholder={t('account.credit.field-placeholder')}
            accessibilityLabel={t('account.credit.field-placeholder')}
            clearButtonType="icon"
            value={promoCode}
            fieldOptionIcon={cameraIcon}
            fieldOptionOnPress={handleShowScanner}
            onChangeText={setPromoCode}
            onSubmit={onPress}
            clearBtnLabel={t('general.clear')}
            notice={
              hasPromoCodeError ? t('account.notice.credit-invalid') : undefined
            }
            invalid={hasPromoCodeError}
            noticePalette="caution"
          />
        </View>
        {!isBottomButton && (
          <Button width={125} {...buttonProps}>
            {t('general.apply')}
          </Button>
        )}
      </View>
      <BodyText size={4}>{t('account.credit-promo.helper-text')}</BodyText>
      {isBottomButton ? (
        <Button size="large" style={styles.buttonMargin} {...buttonProps}>
          {t('general.apply')}
        </Button>
      ) : null}
    </View>
  );
};

export type CreditOverviewPromoCodeFormProps = {
  readonly buttonPlacement?: 'right' | 'bottom';
  readonly onPressApplyPromoCode: (code: string) => void;
  readonly hasPromoCodeError: boolean;
  readonly promoCode: string;
  readonly setPromoCode: (code: string) => void;
  readonly handleShowScanner?: () => void;
  readonly isSubmittingPromoCode?: boolean;
};

const styles = StyleSheet.create({
  promoCodeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing.medium,
  },
  promoCodeInputWrapper: {
    flexGrow: 1,
    marginTop: 30,
  },
  inputPadding: {
    paddingRight: theme.spacing.medium * 2,
  },
  buttonMargin: {
    marginTop: theme.spacing.xlarge,
  },
});
